import { useFormik } from 'formik'
import Input from 'MediaManager/components/Form/Input'
import { createFromDesignLayout } from 'MediaManager/services/packages'
import { getManageProperties, getPropertyById, getUnitType } from 'MediaManager/services/property'
import React, { useCallback, useEffect, useMemo } from 'react'
import Button from 'shared/components/Button'
import Loader from 'shared/components/Loader'
import Select from 'shared/components/Select'
import TinyModal from 'shared/components/TinyModal'
import { useRequest } from 'shared/hooks/useRequest'
import { errorMessages } from 'shared/settings'
import * as Yup from 'yup'

function CreateFromDesignLayout({ onSuccess = () => {}, params = {} }) {
  const [modalShown, setModalShown] = React.useState(false)

  const [properties, setProperties] = React.useState(null)
  const [property, setProperty] = React.useState(null)

  useEffect(() => {
    if (params?.design_layout_id) {
      formik.setValues({
        design_layout_id: Number(params?.design_layout_id),
        vendor_id: Number(params?.vendor_id),
        property_id: Number(params?.property_id),
        unit_type_id: Number(params?.line_id),
        unit_type_level_id: Number(params?.level_id),
      })
    }
  }, [params])

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(errorMessages.required),
    display_name: Yup.string().required(errorMessages.required),
    design_layout_id: Yup.number().required(errorMessages.required),
    price: Yup.string().required(errorMessages.required),
  })

  const formik = useFormik({
    initialValues: {},
    validateOnBlur: true,
    initialTouched: false,
    validationSchema,
    validateOnMount: true,
    onSubmit: async () => {
      try {
        console.log({ values: formik.values })

        const payload = {
          name: formik.values.name,
          display_name: formik.values.display_name,
          price: formik.values.price ? Number(formik.values.price) : null,
          description: formik.values.description,
          design_layout_id: formik.values.design_layout_id,
          vendor_id: formik.values.vendor_id,
        }

        const { data } = await createFromDesignLayout(payload)

        alert('Package created successfully')
        setModalShown(false)
        onSuccess(data.data)
      } catch (error) {
        alert('Error creating package')
      }
    },
  })

  useEffect(() => {
    ;(async () => {
      if (modalShown) {
        const { data: propertiesData } = await getManageProperties()
        setProperties(propertiesData.properties)
      }
    })()
  }, [modalShown])

  const propertiesOptions = React.useMemo(() => {
    if (!properties) {
      return []
    }
    return properties.map((p) => ({
      label: p.property_name,
      value: p.id,
    }))
  }, [properties])

  useEffect(() => {
    ;(async () => {
      if (formik.values?.property_id) {
        const { data: property } = await getPropertyById(formik.values?.property_id)
        setProperty(property.properties_by_id)
      }
    })()
  }, [formik.values?.property_id])

  const unitTypeOptions = React.useMemo(() => {
    if (!property) {
      return []
    }
    return property.unit_types.map((u) => ({
      label: u.name,
      value: u.id,
    }))
  }, [property])

  const unitType = useMemo(() => {
    if (formik.values?.unit_type_id && property) {
      return property.unit_types.find((u) => u.id === formik.values.unit_type_id)
    }
  }, [property, formik.values?.unit_type_id])

  const unitTypeLevelOptions = useMemo(() => {
    if (unitType) {
      return unitType.unit_type_levels.map((l) => ({
        label: `Floor ${l.floor_number}`,
        value: l.id,
      }))
    }
    return []
  }, [unitType])

  const vendorOptions = useMemo(() => {
    if (property) {
      return property.vendors.map((l) => ({
        label: l.full_name,
        value: l.id,
      }))
    }
    return []
  }, [property])

  const designLayoutOptions = useMemo(() => {
    if (unitType) {
      const level = unitType.unit_type_levels.find((l) => l.id === formik.values?.unit_type_level_id)
      return level?.design_layouts.map((l) => ({
        label: `(${l.id}) ${l.name}`,
        value: l.id,
      }))
    }
    return []
  }, [unitType, formik.values?.unit_type_level_id])

  const isCreateFromDesignAuto = params?.design_layout_id

  return (
    <>
      <Button
        rounded
        onClick={() => {
          setModalShown(true)
        }}
      >
        Create from design layout
      </Button>
      {modalShown && (
        <TinyModal
          className="min-w-[500px]"
          title="Create package from design layout"
          closeIcon
          onClose={() => {
            setModalShown(false)
          }}
        >
          {params?.design_layout_id && !unitType ? (
            <Loader />
          ) : (
            <form className="w-full min-w-[500px] px-[22px] max-h-[80dvh] overflow-auto" onSubmit={formik.handleSubmit}>
              <div>
                <Select
                  required
                  options={propertiesOptions}
                  label="Property Name"
                  loading={propertiesOptions.length === 0}
                  value={propertiesOptions.find((o) => o.value === formik.values?.property_id) || null}
                  placeholder="Property Name"
                  isDisabled={isCreateFromDesignAuto}
                  onChange={(val) => {
                    setProperty(null)
                    formik.setValues({
                      ...formik.values,
                      property_id: val.value,
                      unit_type_id: null,
                      unit_type_level_id: null,
                      vendor_id: null,
                      design_layout_id: null,
                    })
                  }}
                />
              </div>
              <div className="mt-[16px]">
                <Select
                  required
                  value={unitTypeOptions.find((o) => o.value === formik.values?.unit_type_id) || null}
                  isDisabled={!formik.values?.property_id || isCreateFromDesignAuto}
                  options={unitTypeOptions}
                  loading={!property && formik.values?.property_id}
                  label="Unit Type ID"
                  placeholder="Unit Type ID"
                  onChange={(val) => {
                    formik.setValues({
                      ...formik.values,
                      unit_type_id: val.value,
                      unit_type_level_id: null,
                      vendor_id: null,
                      design_layout_id: null,
                    })
                  }}
                />
              </div>
              <div className="mt-[16px]">
                <Select
                  required
                  isDisabled={!formik.values?.unit_type_id || isCreateFromDesignAuto}
                  value={unitTypeLevelOptions.find((o) => o.value === formik.values?.unit_type_level_id) || null}
                  label="Unit Type Level ID"
                  options={unitTypeLevelOptions}
                  placeholder="Unit Type Level ID"
                  onChange={(val) => {
                    formik.setValues({
                      ...formik.values,
                      unit_type_level_id: val.value,
                      vendor_id: null,
                      design_layout_id: null,
                    })
                  }}
                />
              </div>
              <div className="mt-[16px]">
                <Select
                  required
                  isDisabled={!formik.values?.unit_type_level_id || isCreateFromDesignAuto}
                  label="Vendor Name"
                  value={vendorOptions.find((o) => o.value === formik.values?.vendor_id) || null}
                  options={vendorOptions}
                  onChange={(val) => {
                    formik.setValues({
                      ...formik.values,
                      vendor_id: val.value,
                      design_layout_id: null,
                    })
                  }}
                  placeholder="Vendor Name"
                />
              </div>
              <div className="mt-[16px]">
                <Select
                  required
                  isDisabled={!formik.values?.vendor_id || isCreateFromDesignAuto}
                  label="Design Layout ID"
                  onChange={(val) => formik.setFieldValue('design_layout_id', val.value)}
                  options={designLayoutOptions}
                  placeholder="Design Layout ID"
                  value={designLayoutOptions?.find((o) => o.value === formik.values?.design_layout_id) || null}
                />
              </div>
              <div className="mt-[16px]">
                <Input
                  wrapClassName="w-full"
                  label="Price"
                  placeholder="Price"
                  number
                  required
                  name="price"
                  onChange={formik.handleChange}
                />
              </div>
              <div className="mt-[16px]">
                <Input
                  wrapClassName="w-full"
                  label="Description"
                  placeholder="Description"
                  name="description"
                  onChange={formik.handleChange}
                />
              </div>
              <div className="mt-[16px]">
                <Input
                  wrapClassName="w-full"
                  label="Display name"
                  placeholder="Display name"
                  name="display_name"
                  required
                  onChange={formik.handleChange}
                />
              </div>
              <div className="mt-[16px]">
                <Input
                  wrapClassName="w-full"
                  label="Name"
                  placeholder="Name"
                  name="name"
                  required
                  onChange={formik.handleChange}
                />
              </div>
              <div className="flex items-center justify-end w-full mt-[16px]">
                <Button size="small" variant="secondary" rounded onClick={() => setModalShown(false)}>
                  Cancel
                </Button>
                <Button disabled={!formik.isValid} type="submit" size="small" rounded className="ml-[16px]">
                  Invoke request
                </Button>
              </div>
            </form>
          )}
        </TinyModal>
      )}
    </>
  )
}

export default CreateFromDesignLayout
